import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import uniqid from 'uniqid';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (!(Symbol.iterator in Object(arr) || Object.prototype.toString.call(arr) === "[object Arguments]")) {
    return;
  }

  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

var colors = {
  // Core
  darkCore: '#ff5e00',
  core: '#fe8c00',
  sunlight: '#fff496',
  // Sale
  rouge: '#ff5a52',
  // Label
  warmBlack: '#323220',
  grass: '#158620',
  // Primary colors
  wave: '#3d6778',
  lightTeal: '#b0ccca',
  silver: '#d6e2e5',
  olive: '#70763d',
  lightOlive: '#c0cba2',
  hazel: '#8f5000',
  sand: '#eee1d2',
  // UI colors
  black: '#000',
  almostblack: '#202020',
  darkGray: '#454545',
  darkGrayL1: '#707070',
  mediumGray: '#adadad',
  mediumGrayL1: '#c7c7c7',
  lightGray: '#e3e3e3',
  lightGrayL1: '#f7f7f7',
  white: '#fff',
  lightSand: '#f9f5f1',
  lightSandBox: '#fdf8cf',
  ocean: '#359cbb',
  oceanL1: '#60b9d4',
  morello: '#e42a25',
  morelloL1: '#e74b3f',
  spreadShadow: '#dfdfdf',
  // Globetrotter
  darkGlobe: '#172b54',
  darkGlobeL1: '#3a4b6d',
  globe: '#7594b3',
  globeL1: '#89a4be',
  latte: '#a48c6a',
  lightRouge: '#fabaac',
  mora: '#4c1029',
  moraL1: '#673448',
  // Spies
  mango: '#f6b637',
  // Other
  tripadvisor: '#589442',
  // Validation error
  validationErrorBg: '#facac7',
  validationErrorText: '#8f271f',
  validationErrorBorder: '#fd9e99',
  // Validation
  validationValid: '#1d9886'
};

var transitionSpeeds = {
  "default": '.25s',
  smooth: '.5s',
  slow: '1s'
};
var transitions = {
  "default": "".concat(transitionSpeeds["default"], " ease"),
  smooth: "".concat(transitionSpeeds.smooth, " ease"),
  slow: "".concat(transitionSpeeds.slow, " ease")
};

var Link = styled.a.withConfig({
  displayName: "style__Link",
  componentId: "sc-ihea2g-0"
})(["color:", ";cursor:pointer;overflow:visible;text-align:left;display:flex;align-items:center;"], colors.ocean);
var LinkText = styled.span.withConfig({
  displayName: "style__LinkText",
  componentId: "sc-ihea2g-1"
})(["border-bottom:", " solid 1px;color:", ";transition:border-color ", ",color ", ";", " ", " & + .sg-link-icon{fill:", ";margin-left:3px;", "}.no-touch &:hover{position:relative;border-color:", ";color:", ";fill:", ";}"], colors.ocean, colors.ocean, transitions["default"], transitions["default"], function (props) {
  return props.hasIcon && css(["border-color:transparent;"]);
}, function (props) {
  return props.hasLeadingIcon && css(["order:2;"]);
}, colors.ocean, function (props) {
  return props.hasLeadingIcon && css(["margin:0 3px 0 0;"]);
}, colors.oceanL1, colors.oceanL1, colors.oceanL1);
var LinkList = styled.ul.withConfig({
  displayName: "style__LinkList",
  componentId: "sc-ihea2g-2"
})(["li{line-height:20px;max-width:400px;}"]);

var Link$1 = function Link$1(props) {
  var className = props.className,
      children = props.children,
      name = props.name,
      href = props.href,
      onClick = props.onClick,
      gtm = props.gtm,
      ariaExpanded = props.ariaExpanded,
      icon = props.icon,
      hasLeadingIcon = props.hasLeadingIcon,
      rest = _objectWithoutProperties(props, ["className", "children", "name", "href", "onClick", "gtm", "ariaExpanded", "icon", "hasLeadingIcon"]);

  if ((typeof href !== 'string' || !href) && !onClick) {
    return false;
  }

  var elementName = name || children || href || undefined;
  var renderType = href ? 'a' : 'button';
  return React.createElement(Link, _extends({
    className: className,
    name: elementName,
    onClick: onClick,
    type: renderType === 'button' ? 'button' : null,
    as: renderType,
    "aria-expanded": ariaExpanded || null,
    href: href,
    "data-gtm-tag": gtm
  }, rest), React.createElement(React.Fragment, null, React.createElement(LinkText, {
    "data-gtm-tag": gtm,
    hasIcon: !!icon,
    hasLeadingIcon: hasLeadingIcon
  }, children), icon && React.createElement("span", {
    className: "sg-link-icon"
  }, icon)));
};

Link$1.propTypes = {
  className: PropTypes.string,
  ariaExpanded: PropTypes.string,
  children: PropTypes.node,
  href: PropTypes.string,
  onClick: PropTypes.func,
  gtm: PropTypes.string,
  rest: PropTypes.node,
  name: PropTypes.string,
  icon: PropTypes.node,
  hasLeadingIcon: PropTypes.bool
};
Link$1.defaultProps = {
  className: null,
  ariaExpanded: null,
  children: null,
  href: null,
  onClick: null,
  gtm: null,
  rest: null,
  name: null,
  icon: null,
  hasLeadingIcon: false
};

var Icon = styled.svg.withConfig({
  displayName: "style__Icon",
  componentId: "sc-sizmc5-0"
})(["display:inline-block;height:22px;width:22px;", " ", " ", " ", " ", " ", ""], function (props) {
  return props.size === 'xxxs' && css(["height:10px;width:10px;"]);
}, function (props) {
  return props.size === 'xxs' && css(["height:12px;width:12px;"]);
}, function (props) {
  return (props.size === 'xs' || props.size === 'x-small') && css(["height:16px;width:16px;"]);
}, function (props) {
  return (props.size === 'm' || props.size === 'medium') && css(["height:32px;width:32px;"]);
}, function (props) {
  return (props.size === 'l' || props.size === 'large') && css(["height:48px;width:48px;"]);
}, function (props) {
  return (props.size === 'xl' || props.size === 'x-large') && css(["height:64px;width:64px;"]);
});

var Icon$1 = function Icon$1(_ref) {
  var path = _ref.path,
      pathMobile = _ref.pathMobile,
      size = _ref.size,
      sizeMobile = _ref.sizeMobile,
      className = _ref.className,
      rest = _objectWithoutProperties(_ref, ["path", "pathMobile", "size", "sizeMobile", "className"]);

  var isClient = typeof window !== 'undefined';

  var _useState = useState({
    smallScreen: isClient && window && window.innerWidth < 768
  }),
      _useState2 = _slicedToArray(_useState, 2),
      state = _useState2[0],
      setState = _useState2[1];

  var handleResize = useCallback(function () {
    setState({
      smallScreen: isClient && window && window.innerWidth < 768
    });
  });
  useEffect(function () {
    if (!isClient) {
      return false;
    }

    window.addEventListener('resize', handleResize);
    return function () {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);
  var iconSize = state.smallScreen && sizeMobile ? sizeMobile : size;
  var iconPath = state.smallScreen && pathMobile ? pathMobile : path;

  if (!path) {
    return false;
  }

  return React.createElement(Icon, _extends({
    viewBox: "0 0 32 32",
    xmlns: "https://www.w3.org/2000/svg",
    className: className,
    size: iconSize
  }, rest), React.createElement("path", {
    className: "sg-icon__path",
    d: iconPath
  }));
};

var sizes = ['xxxs', 'xxs', 'xs', 'x-small', 'm', 's', 'small', 'medium', 'l', 'large', 'xl', 'x-large'];
Icon$1.propTypes = {
  size: PropTypes.oneOf(sizes),
  sizeMobile: PropTypes.oneOf(sizes),
  path: PropTypes.string,
  pathMobile: PropTypes.string,
  className: PropTypes.string
};
Icon$1.defaultProps = {
  size: null,
  sizeMobile: null,
  path: null,
  pathMobile: null,
  className: null
};

/* eslint-disable max-len */
var USER_INTERFACE_ARROW_04_DOWN = 'M16,25.324a1.257,1.257,0,0,1-.947-.363c-.058-.058-.042-.144-.086-.209L.372,8.846a1.271,1.271,0,0,1,1.8-1.8L16,22.126,29.831,7.047a1.271,1.271,0,1,1,1.8,1.8l-14.585,15.9c-.046.068-.03.157-.09.216A1.254,1.254,0,0,1,16,25.324Z';
var USER_INTERFACE_ARROW_04_LEFT = 'M6.676,16a1.257,1.257,0,0,1,.363-.947c.058-.058.144-.042.209-.086L23.154.372a1.271,1.271,0,1,1,1.8,1.8L9.874,16,24.953,29.831a1.271,1.271,0,1,1-1.8,1.8L7.254,17.043c-.068-.046-.157-.03-.216-.09A1.254,1.254,0,0,1,6.676,16Z';
var USER_INTERFACE_ARROW_04_RIGHT = 'M25.324,16a1.257,1.257,0,0,1-.363.947c-.058.058-.144.042-.209.086L8.846,31.628a1.271,1.271,0,0,1-1.8-1.8L22.126,16,7.047,2.169a1.271,1.271,0,1,1,1.8-1.8l15.9,14.585c.068.046.157.03.216.09A1.254,1.254,0,0,1,25.324,16Z';
var USER_INTERFACE_ARROW_04_UP = 'M16,6.676a1.257,1.257,0,0,1,.947.363c.058.058.042.144.086.209L31.628,23.154a1.271,1.271,0,1,1-1.8,1.8L16,9.874,2.169,24.953a1.271,1.271,0,1,1-1.8-1.8l14.585-15.9c.046-.068.03-.157.09-.216A1.254,1.254,0,0,1,16,6.676Z';

var ArrowLink = function ArrowLink(props) {
  var children = props.children;
  return React.createElement(Link$1, _extends({}, props, {
    icon: React.createElement(Icon$1, {
      path: USER_INTERFACE_ARROW_04_RIGHT,
      size: "xxs"
    })
  }), children);
};

ArrowLink.propTypes = {
  children: PropTypes.node
};
ArrowLink.defaultProps = {
  children: null
};

var BackLink = function BackLink(props) {
  var children = props.children;
  return React.createElement(Link$1, _extends({}, props, {
    hasLeadingIcon: true,
    icon: React.createElement(Icon$1, {
      path: USER_INTERFACE_ARROW_04_LEFT,
      size: "xxs"
    })
  }), children);
};

BackLink.propTypes = {
  children: PropTypes.node
};
BackLink.defaultProps = {
  children: null
};

var ExpandableLink = function ExpandableLink(props) {
  var children = props.children,
      isOpen = props.isOpen,
      rest = _objectWithoutProperties(props, ["children", "isOpen"]);

  var iconPath = isOpen ? USER_INTERFACE_ARROW_04_UP : USER_INTERFACE_ARROW_04_DOWN;
  return React.createElement(Link$1, _extends({
    ariaExpanded: isOpen ? 'true' : 'false',
    icon: React.createElement(Icon$1, {
      path: iconPath,
      size: "xxs"
    })
  }, rest), children);
};

ExpandableLink.propTypes = {
  children: PropTypes.node,
  isOpen: PropTypes.bool
};
ExpandableLink.defaultProps = {
  children: null,
  isOpen: false
};

var ListLink = function ListLink(_ref) {
  var className = _ref.className,
      links = _ref.links;

  if (!links || links.length < 1) {
    return false;
  }

  var linkList = links.map(function (item) {
    if (!item || !item.href) return null;
    return React.createElement("li", {
      key: uniqid('link-')
    }, React.createElement(Link$1, {
      href: item.href,
      target: item.target,
      rel: item.rel || null,
      hasLeadingIcon: true,
      icon: React.createElement(Icon$1, {
        path: USER_INTERFACE_ARROW_04_RIGHT,
        size: "xxs"
      })
    }, item.name));
  });

  if (!linkList.length) {
    return false;
  }

  return React.createElement(LinkList, {
    className: className
  }, linkList);
};

ListLink.propTypes = {
  className: PropTypes.string,
  links: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    href: PropTypes.string,
    target: PropTypes.string
  })).isRequired
};
ListLink.defaultProps = {
  className: null
};

export { ArrowLink, BackLink, ExpandableLink, Link$1 as Link, ListLink };
//# sourceMappingURL=index.js.map
